import { cleanEntries, entriesOf, saveContactMethods } from './contactMethods';

// BL-177 PR D: MY CONTACTS - a private, per-person address book. The database (RLS) is the privacy: nobody but the owner - not company
// admins, not platform admins - can read these rows; they are shared only by the owner, per contact or as the whole list, View or Edit.
// This file is the browser's thin layer over those tables and functions, plus the pure name rules for the inbox.

export const PERSONAL_TABLE = 'personal_contacts';
export const PERSONAL_METHODS_TABLE = 'personal_contact_methods';

export const CRM_TABS = Object.freeze([
    { key: 'company', label: 'Company' },
    { key: 'mine', label: 'My Contacts' },
    { key: 'shared', label: 'Shared with me' },
]);

/** Where a new contact is saved. Company contacts is the default; New people from the company inbox always go to Company contacts. */
export const SAVE_TARGETS = Object.freeze([
    { key: 'company', label: 'Company contacts', hint: 'Everyone at this company with access can see it.' },
    { key: 'mine', label: 'My Contacts', hint: 'Private to you. Not even admins can see it unless you share it.' },
]);

export const ACCESS_LABELS = Object.freeze({ view: 'View only', edit: 'Can edit' });

const SELECT = '*, contact_methods:personal_contact_methods(id, kind, label, value, address, is_primary, created_at)';

/** { enabled, sharing_enabled, platform:{...}, company:{...} } - what the screens ask before showing anything. Fails closed. */
export async function fetchPersonalContactsStatus(client) {
    try {
        const { data, error } = await client.rpc('personal_contacts_status');
        if (error || !data) return { enabled: false, sharing_enabled: false, platform: null, company: null };
        return {
            enabled: data.enabled === true,
            sharing_enabled: data.sharing_enabled === true,
            platform: data.platform || null,
            company: data.company || null,
        };
    } catch {
        return { enabled: false, sharing_enabled: false, platform: null, company: null };
    }
}

/** Colleagues the caller may share with, newest rules applied by the database (empty while sharing is off). */
export async function fetchColleagues(client) {
    const { data, error } = await client.rpc('personal_contacts_colleagues');
    if (error) throw error;
    return (data || []).map(row => ({ id: row.user_id, name: row.full_name }));
}

/** Everything the signed-in person's list holds (own contacts) - never anyone else's. */
export async function fetchMyContacts(client, userId) {
    const { data, error } = await client.from(PERSONAL_TABLE).select(SELECT)
        .eq('owner_id', userId).is('locked_at', null).order('name');
    if (error) throw error;
    return data || [];
}

/** Contacts other people shared with the signed-in person (RLS already limits this to active shares). */
export async function fetchSharedWithMe(client, userId) {
    const { data, error } = await client.from(PERSONAL_TABLE).select(SELECT)
        .neq('owner_id', userId).is('locked_at', null).order('name');
    if (error) throw error;
    return data || [];
}

/**
 * Lightweight rows that may NAME a conversation on the signed-in person's own screens: their own My Contacts first, then the ones colleagues
 * SHARED WITH THEM (row-level security returns only active shares, with sharing on and the owner not separated). Own first so the order
 * "my own > shared with me" holds wherever the list is searched.
 */
export async function fetchNamingContacts(client, userId) {
    const { data, error } = await client.from(PERSONAL_TABLE).select('id, name, phones, emails, owner_id').is('locked_at', null);
    if (error) throw error;
    return (data || []).slice().sort((a, b) => (b.owner_id === userId ? 1 : 0) - (a.owner_id === userId ? 1 : 0));
}

const blank = (value) => (typeof value === 'string' ? value.trim() : '');

/** Create one My Contact with all of its labeled entries. The first entry of each kind inserted is the Primary. */
export async function createPersonalContact(client, { form, orgId, ownerId }) {
    const name = blank(form.name);
    if (!name) throw new Error('A contact needs a name.');
    const { data: contact, error } = await client.from(PERSONAL_TABLE).insert([{
        org_id: orgId,
        owner_id: ownerId,
        name,
        company_name: blank(form.company_name) || null,
        position: blank(form.position) || null,
        notes: blank(form.notes) || null,
        tags: Array.isArray(form.tags) ? form.tags : [],
        source: 'Staff entry',
    }]).select('id').single();
    if (error) throw error;
    for (const [kind, key] of [['phone', 'phones'], ['email', 'emails'], ['address', 'addresses']]) {
        const rows = cleanEntries(form[key], kind);
        const ordered = [...rows.filter(r => r.is_primary), ...rows.filter(r => !r.is_primary)];
        for (const entry of ordered) {
            const { error: entryError } = await client.from(PERSONAL_METHODS_TABLE).insert([{
                contact_id: contact.id, kind, label: entry.label, value: entry.value, is_primary: entry.is_primary,
            }]);
            if (entryError) throw entryError;
        }
    }
    return contact.id;
}

/** Save the contact's own fields and the diff of each phone / email / address list. */
export async function savePersonalContact(client, { contact, draft }) {
    const { error } = await client.from(PERSONAL_TABLE).update({
        name: blank(draft.name) || contact.name,
        company_name: blank(draft.company_name) || null,
        position: blank(draft.position) || null,
        notes: blank(draft.notes) || null,
    }).eq('id', contact.id);
    if (error) throw error;
    for (const [kind, key] of [['phone', 'phones'], ['email', 'emails'], ['address', 'addresses']]) {
        await saveContactMethods(client, {
            contactId: contact.id, kind, original: entriesOf(contact, kind), next: draft[key], tableName: PERSONAL_METHODS_TABLE,
        });
    }
}

export async function deletePersonalContact(client, id) {
    const { error } = await client.from(PERSONAL_TABLE).delete().eq('id', id);
    if (error) throw error;
}

// ---------------------------------------------------------------------------------------------- sharing
/** Active shares the owner has given (contact_id null = the whole list). */
export async function fetchMyShares(client, userId) {
    const { data, error } = await client.from('personal_contact_shares')
        .select('id, contact_id, shared_with, access, created_at').eq('owner_id', userId).is('revoked_at', null).order('created_at');
    if (error) throw error;
    return data || [];
}

export async function shareWith(client, { orgId, ownerId, contactId = null, userId, access = 'view' }) {
    const { data, error } = await client.from('personal_contact_shares')
        .insert([{ org_id: orgId, owner_id: ownerId, contact_id: contactId, shared_with: userId, access }]).select('id').single();
    if (error) throw error;
    return data.id;
}

export async function changeShareAccess(client, shareId, access) {
    const { error } = await client.from('personal_contact_shares').update({ access }).eq('id', shareId);
    if (error) throw error;
}

export async function revokeShare(client, shareId) {
    const { error } = await client.from('personal_contact_shares').update({ revoked_at: new Date().toISOString() }).eq('id', shareId);
    if (error) throw error;
}

/** "Shared by Alice": the owner's name from the colleague list (the database returns only people in the same company). */
export function sharedByName(contact, colleagues) {
    return (colleagues || []).find(person => person.id === contact?.owner_id)?.name || 'a colleague';
}

/** Can the signed-in person edit this contact? Owners always; a colleague only with a Can-edit share. */
export function canEditPersonal(contact, userId, shares = []) {
    if (!contact) return false;
    if (contact.owner_id === userId) return true;
    return shares.some(share => share.access === 'edit' && (share.contact_id === contact.id || share.contact_id === null) && share.owner_id === contact.owner_id);
}

// ---------------------------------------------------------------------------------------------- add to company
/**
 * "Add to company contacts" on a My Contact: an ADD (open to everyone with CRM access at that company) after the same duplicate check
 * as the Add dialog. Returns { duplicates } and writes nothing when someone already matches and `allowDuplicate` is not set.
 * The private copy stays.
 */
export async function addPersonalToCompany(client, { contactId, entityId, allowDuplicate = false }) {
    const { data, error } = await client.rpc('personal_contact_add_to_company', {
        p_contact: contactId, p_entity: entityId, p_allow_duplicate: allowDuplicate,
    });
    if (error) throw error;
    if (data?.status === 'duplicates') return { duplicates: data.duplicates || [] };
    return { contactId: data?.contact_id, accountId: data?.account_id };
}

// ---------------------------------------------------------------------------------------------- own-list import
/** Preview (apply=false: nothing written) or apply an import into the signed-in person's OWN list. */
export async function runPersonalImport(client, { rows, apply, source = 'CSV import' }) {
    const { data, error } = await client.rpc('personal_contacts_import_run', { p_rows: rows, p_apply: apply, p_source: source });
    if (error) throw error;
    return data;
}

// ---------------------------------------------------------------------------------------------- separated employees
/** Admins see ONLY a count per separated person: their My Contacts are locked, nobody can open, export or move them. */
export async function fetchLockedSummary(client) {
    const { data, error } = await client.rpc('personal_contacts_locked_summary');
    if (error) throw error;
    return (data || []).map(row => ({ ownerId: row.owner_id, name: row.owner_name, locked: row.locked_count }));
}

// ---------------------------------------------------------------------------------------------- names in the inbox
const digits = (value) => String(value || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
const emailKey = (value) => String(value || '').trim().toLowerCase();

/**
 * The My Contact that names a conversation for the signed-in person (any phone, then any email), or null. `contacts` is their own list
 * followed by what colleagues shared with them (fetchNamingContacts), so their own wins.
 * Name precedence everywhere (list, header, group members): company contact > the company account's name > MY My Contact > a My Contact
 * SHARED WITH ME > the number. A My Contact never hides, owns or redirects a company conversation: it only supplies a name when nothing
 * in the company does, and only on the screens of the people it belongs to or was shared with (row-level security decides who).
 */
export function findMyContactForSession(session, myContacts = []) {
    if (!session || !Array.isArray(myContacts) || !myContacts.length) return null;
    const phone = digits(session.contact_phone);
    const email = emailKey(session.contact_email);
    return myContacts.find((contact) => (
        (phone && (contact.phones || []).some(p => digits(typeof p === 'string' ? p : p?.number) === phone))
        || (email && (contact.emails || []).some(e => emailKey(typeof e === 'string' ? e : e?.value) === email))
    )) || null;
}

/** The text to show for a conversation: the company's name first, then (only for me) My Contact's name, then the address. */
export function displayNameWithMyContacts({ session, account = null, contact = null, myContact = null }) {
    return blank(contact?.name) || blank(account?.account_name) || blank(myContact?.name)
        || blank(session?.contact_email) || blank(session?.contact_phone) || 'Unknown contact';
}

// ---------------------------------------------------------------------------------------------- lists on screen
/** Search across name, company, position, tags and every phone / email (digits-only match for phones). */
export function filterPersonal(contacts, search) {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return contacts || [];
    const digitsQuery = q.replace(/\D/g, '');
    return (contacts || []).filter((contact) => {
        const phones = (contact.phones || []).map(String);
        if (digitsQuery.length >= 3 && phones.some(p => p.replace(/\D/g, '').includes(digitsQuery))) return true;
        return [contact.name, contact.company_name, contact.position, ...(contact.emails || []), ...phones, ...(contact.tags || [])]
            .some(value => String(value || '').toLowerCase().includes(q));
    });
}

/** Shares given TO the signed-in person (what they may edit), newest rules from the database. */
export async function fetchIncomingShares(client, userId) {
    const { data, error } = await client.from('personal_contact_shares')
        .select('id, owner_id, contact_id, access').eq('shared_with', userId).is('revoked_at', null);
    if (error) throw error;
    return data || [];
}

/** The effective access of the signed-in person on one contact: 'owner' | 'edit' | 'view'. */
export function accessOn(contact, userId, incoming = []) {
    if (contact?.owner_id === userId) return 'owner';
    const mine = incoming.filter(share => share.owner_id === contact?.owner_id && (share.contact_id === contact?.id || share.contact_id === null));
    return mine.some(share => share.access === 'edit') ? 'edit' : 'view';
}
